/* eslint-disable max-len */
// utils/solutionUtils.ts

import {isEmpty} from 'lodash';


/** normalizeSolutions 返回值类型 */
export interface NormalizeSolutionsResult {
    /** 标准化后的解列表 */
    solutions: InitialSolutionFileInfo[];
    /** s_id -> solution 的映射 */
    initialSolutionsArray: InitialSolutionFileInfo[];
}


export function normalizeSolutions(
    data: Partial<ExtraData>,
    stageInfoName: JobStageInfoName
): NormalizeSolutionsResult {
    // 默认返回值
    const emptyResult: NormalizeSolutionsResult = {
        solutions: [],
        initialSolutionsArray: []
    };
    if (!data) {
        return emptyResult;
    }

    const {initial_solutions = {}, list_relationships = [], path_relationships = {}} = data;
    if (isEmpty(initial_solutions) || !list_relationships || !path_relationships) {
        return emptyResult;
    }
    const initialSolutionsArray: InitialSolutionFileInfo[] = Object.values(initial_solutions);

    // 提前构建 s_id -> solution 的映射，避免反复 find
    const solutionMap = new Map(initialSolutionsArray.map(s => [s.s_id, s]));

    const allList_relationships = list_relationships
        .map((id, index) => {
            const solution = solutionMap.get(id);
            if (!solution) {
                return null;
            }

            let evolve_path: PathInfo[] = [];
            if ([TaskStage.REPORT, TaskStage.SELECT].includes(stageInfoName)) {
                const dataKey = stageInfoName === TaskStage.SELECT ? 'evolve_path_relationships' : 'path_relationships';
                // 构建 evolve_path 演化路径
                const evolveIds = data[dataKey]?.[solution.s_id] || [];
                evolve_path = evolveIds
                    .map(eid => solutionMap.get(eid))
                    .filter(Boolean)
                    .map(rel => ({
                        path: rel.path,
                        iteration_found: String(rel.iteration_found ?? '')
                    }));
            }


            return {
                ...solution,
                evolve_path: evolve_path,
                index
            };
        })
        .filter(Boolean) as InitialSolutionFileInfo[];

    /** 返回全部排序过的所有解详情 */
    /** 返回解列表和映射 */
    return {
        solutions: allList_relationships,
        initialSolutionsArray
    };
}

/**
 * 阶段过滤
 */
export function applyStageFilter(
    list: InitialSolutionFileInfo[],
    stage: JobStageInfoName,
    filter: any
): InitialSolutionFileInfo[] {
    if (!filter) {
        return list;
    }

    let result = list;

    if (stage === 'COLDSTART') {
        if (filter.is_initial_solution) {
            result = result.filter(item => item.is_initial_solution);
        }
        if (filter.source?.length) {
            result = result.filter(item => filter.source.includes(item.source));
        }
    }

    return result;
}

/**
 * 构建阶段数据
 * 将原始阶段信息转换为缓存所需的格式
 *
 * @param stageInfo - 阶段原始信息
 * @param stageInfoName - 阶段名称
 * @param filterList - 过滤后的解决方案列表
 * @param fullList - 完整的解决方案列表
 * @param formatDuration - 格式化时长的函数
 * @returns 格式化后的阶段数据
 */
export function buildStageData(
    stageInfo: ExtraData | null,
    stageInfoName: JobStageInfoName,
    filterList: InitialSolutionFileInfo[],
    fullList: InitialSolutionFileInfo[],
    formatDuration: (seconds: number) => string
): any {
    if (!stageInfo) {
        return null;
    }

    const duration = typeof stageInfo.duration === 'number' ? stageInfo.duration : 0;
    const baseData = {
        ...stageInfo,
        duration: formatDuration(duration)
    };

    switch (stageInfoName) {
        case 'COLDSTART':
        case 'EVOLVE':
            return {
                ...baseData,
                initial_solution_set: filterList
            };
        case 'REPORT':
            return {
                ...baseData,
                initial_solution_set: fullList.filter((_, index) => index < 3)
            };
        default:
            return baseData;
    }
}

/**
 * 为演化阶段构建请求参数
 * 应用存储的筛选条件到请求参数中
 *
 * @param params - 基础参数对象
 * @param evolveTopValue - 演化阶段的 top 值
 * @param evolveIsShowTrackSolution - 是否显示跟踪解决方案
 * @param evolveSelectedIslands - 选中的岛屿ID列表
 */
export function buildEvolveStageParams(
    params: any,
    evolveTopValue: number | null,
    evolveIsShowTrackSolution: boolean,
    evolveSelectedIslands: string[]
): void {
    if (evolveTopValue && evolveTopValue !== 20) {
        params.top = evolveTopValue;
    }
    if (evolveIsShowTrackSolution) {
        params.concise = evolveIsShowTrackSolution;
    }
    if (evolveSelectedIslands.length > 0) {
        params.islands = evolveSelectedIslands.map(item => parseInt(item, 10));
    }
}

/**
 * 生成去重的岛屿列表
 * @param islandScore - 岛屿评分数据
 * @returns 排序后的岛屿列表
 */
export function generateUniqueIslandList(
    islandScore: IslandScoreType
) {
    if (!islandScore || Object.keys(islandScore).length === 0) {
        return [];
    }

    // 收集唯一的岛屿ID并排序
    const islandIds = Object.keys(islandScore)
        .map(Number)
        .sort((a, b) => a - b);

    // 生成排序后的岛屿列表
    return islandIds.map((islandId, index) => ({
        index,
        label: `岛屿${islandId}`,
        value: islandId
    }));
}
